/*
HELPER UTILITIES FOR COLORS

- COLOR TO THREE.JS COLOR CACHE
- LIGHTENING OF COLORS
- DARKENING OF COLORS

Motivation:

In high performance code we should not be creating one three.color instance all the time because of overhead. Also we should not
call functions to darken or lighten a color for every chair.

In order to speed up these operations, this module will keep a cache of different operations on colors so that the actual calculation
only takes place if we have a cache miss.

We rely on javascript optimizer to

*/

import { Color } from 'three';
import tinycolor from 'tinycolor2';

const DARKEN_AMOUNT = 10;
const LIGHTEN_AMOUNT = 20;

const _threeColorCache = {};
const _bitmapColorCache = {};
const _isDarkCache = {};
const _lightenedCache = {};
const _darkenedCache = {};

const getThreeColor = (colorStr) => {
    let threeColor = _threeColorCache[colorStr];

    if (!threeColor) {
        threeColor = new Color(colorStr);
        _threeColorCache[colorStr] = threeColor;
    }
    return threeColor;
};

const getBitmapColor = (hex) => {
    let result = _bitmapColorCache[hex];

    if (!result) {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = 255 & bigint;

        result = { b, g, r };
        _bitmapColorCache[hex] = result;
    }
    return result;
};

const isDark = (colorStr) => {
    let isDarkValue = _isDarkCache[colorStr];

    if (!isDarkValue) {
        isDarkValue = tinycolor(colorStr).isDark();
        _isDarkCache[colorStr] = isDarkValue;
    }
    return isDarkValue;
};

const _clamp01 = (val) => Math.min(1, Math.max(0, val));

const getDarkColor = (colorStr) => {
    let darkValue = _darkenedCache[colorStr];

    if (!darkValue) {
        const hsl = tinycolor(colorStr).toHsl();

        hsl.l -= DARKEN_AMOUNT / 100;
        hsl.l = _clamp01(hsl.l);
        darkValue = tinycolor(hsl).toHexString();
        _darkenedCache[colorStr] = darkValue;
    }
    return darkValue;
};

const getLightColor = (colorStr) => {
    let lightValue = _lightenedCache[colorStr];

    if (!lightValue) {
        const hsl = tinycolor(colorStr).toHsl();

        hsl.l += LIGHTEN_AMOUNT / 100;
        hsl.l = _clamp01(hsl.l);
        lightValue = tinycolor(hsl).toHexString();
        _lightenedCache[colorStr] = lightValue;
    }
    return lightValue;
};

export default {
    getBitmapColor,
    getDarkColor,
    getLightColor,
    getThreeColor,
    isDark,
};
